/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, useCallback, useContext, useState } from "react";
import { BEM, ErrorTracker } from "@adapptio/lib-components";

import { Panel, PANEL_STYLE, PanelNote } from "../../../../ui/components/Panel";
import { ILayoutConfig, LAYOUT_TYPE } from "../../../../ui/components/Layout/LayoutTypes";
import { Layout } from "../../../../ui/components/Layout/Layout";
import { LayoutState } from "../../../../ui/components/Layout/LayoutState";
import { useBlueprintModel } from "../../../../ui/hooks/useBlueprintModel";
import { useSimpleEvent } from "../../../../ui/hooks/useSimpleEvent";
import {
	FieldGroup,
	FieldLabel,
	FieldRow,
	FieldSection,
	FieldSectionContent,
	FieldSectionHeader,
	FieldText
} from "@adapptio/lib-editor-ui";
import { Inspector } from "../../../../inspector";
import { ViewEditorView } from "../ViewEditorView";
import { TOAST_TYPE } from "../../../../toasts/IToast";
import { EditorMainContext } from "../../../../ui/contexts/EditorMainContext";
import { FieldButton } from "../../../../ui/components/Fields/FieldButton";
import { ExpressionEditor } from "./ViewEditorExpressionEditor";
import { SCHEMA_VALUE_TYPE } from "@adapptio/lib-blueprint";

export const ViewEditorTesterLayout: ILayoutConfig = {
	root: {
		type: LAYOUT_TYPE.ROW,
		id: "layout",
		children: [
			{
				type: LAYOUT_TYPE.COLUMN,
				id: "panelLeft",
				size: "300px",
				isExpandable: false,
				defaultExpanded: true,
				children: [
					{
						type: LAYOUT_TYPE.PANEL,
						id: "parameters",
						isExpandable: false,
						contentComponent: "parameters"
					}
				]
			},
			{
				type: LAYOUT_TYPE.SPLITTER,
				id: "leftSplitter"
			},
			{
				type: LAYOUT_TYPE.COLUMN,
				id: "main",
				children: [
					{
						type: LAYOUT_TYPE.PANEL,
						id: "output",
						size: "300px",
						isExpandable: false,
						contentComponent: "output"
					}
				]
			}
		]
	}
};

export interface IViewEditorTesterContentProps {
	view: ViewEditorView;
}

export const ViewEditorTesterContent: FC<IViewEditorTesterContentProps> = ({ view }) => {
	const [ layoutState ] = useState(() => new LayoutState(ViewEditorTesterLayout));
	const model = view.getViewModel();
	const editorCtx = useContext(EditorMainContext);

	const [ paramsModelRef, setParamsModelRef ] = useState(() => model.getParamsModel());

	const [ paramsModelError, setParamsModelError ] = useState(() => model.getParamsModelError());
	const [ location, setLocation ] = useState(() => model.getRoutingManager().getCurrentLocationAsString());

	const { model: paramsModel } = useBlueprintModel(paramsModelRef);

	const [ isEventBeingTriggered, setIsEventBeingTriggered ] = useState({
		onInit: false,
		onParamsChanged: false
	});

	const navigate = useCallback(
		(value: string) => {
			model.getRoutingManager().navigate(value);
		},
		[ model, model?.getRoutingManager() ]
	);

	const triggerOnInitViewEvent = useCallback(() => {
		setIsEventBeingTriggered((s) => ({ ...s, onInit: true }));

		const loadingToastId = editorCtx.toastService.add({
			type: TOAST_TYPE.LOADING,
			// @todo Translate
			message: "Executing On Initialize event...",
			isClosable: false
		});

		model
			.triggerOnInitEvent()
			.catch((err) => {
				const errorTracker = ErrorTracker.getInstance();
				errorTracker.captureException(err);

				editorCtx.toastService.add({
					type: TOAST_TYPE.ERROR,
					// @todo Translate
					message: "Failed to execute On Initialize event",
					details: String(err),
					isClosable: true
				});
			})
			.finally(() => {
				setIsEventBeingTriggered((s) => ({ ...s, onInit: false }));
				editorCtx.toastService.remove(loadingToastId);
			});
	}, [ model, editorCtx, setIsEventBeingTriggered ]);

	const triggerOnParamsChangedViewEvent = useCallback(() => {
		setIsEventBeingTriggered((s) => ({ ...s, onParamsChanged: true }));

		const loadingToastId = editorCtx.toastService.add({
			type: TOAST_TYPE.LOADING,
			// @todo Translate
			message: "Executing On Params Changed event...",
			isClosable: false
		});

		model
			.triggerOnParamsChangedEvent()
			.catch((err) => {
				const errorTracker = ErrorTracker.getInstance();
				errorTracker.captureException(err);

				editorCtx.toastService.add({
					type: TOAST_TYPE.ERROR,
					// @todo Translate
					message: "Failed to execute On Params Changed event",
					details: String(err),
					isClosable: true
				});
			})
			.finally(() => {
				setIsEventBeingTriggered((s) => ({ ...s, onParamsChanged: false }));
				editorCtx.toastService.remove(loadingToastId);
			});
	}, [ model, editorCtx, setIsEventBeingTriggered ]);

	useSimpleEvent(
		model.onParamsModelBuilt,
		() => {
			setParamsModelRef(model.getParamsModel());
			setParamsModelError(model.getParamsModelError());
		},
		[],
		true,
		true
	);

	useSimpleEvent(
		model.getRoutingManager().onNavigate,
		() => setLocation(model.getRoutingManager().getCurrentLocationAsString()),
		[ model, model?.getRoutingManager() ],
		true
	);

	const renderComponent = (componentName: string) => {
		switch (componentName) {
			case "parameters": {
				return (
					<Panel style={PANEL_STYLE.PRIMARY} scroll={true}>
						<Inspector model={paramsModel} label="Test Parameters" group>
							{paramsModelError !== undefined && (
								<>
									<PanelNote>
										WARNING: Action parameters have invalid configuration.
									</PanelNote>
								</>
							)}
							{
								paramsModel &&
								paramsModel.type === SCHEMA_VALUE_TYPE.CONST && Object.keys(paramsModel.constant.props).length === 0 && (
									<PanelNote>No parameters have been configured yet.</PanelNote>
								)
							}
						</Inspector>

						<FieldSection stateKey="testLocation" collapsible group defaultCollapsed={false}>
							<FieldSectionHeader>
								<FieldLabel>Test Location</FieldLabel>
							</FieldSectionHeader>
							<FieldSectionContent disablePadding>
								<FieldRow>
									<FieldLabel>Location URL</FieldLabel>
									<FieldGroup grow>
										<FieldText value={location} onChange={navigate} grow={true} />
									</FieldGroup>
								</FieldRow>
							</FieldSectionContent>
						</FieldSection>

						<FieldSection stateKey="testEvents" collapsible group defaultCollapsed={false}>
							<FieldSectionHeader>
								<FieldLabel>Test View Events</FieldLabel>
							</FieldSectionHeader>
							<FieldSectionContent>
								<FieldRow>
									<FieldGroup grow>
										<FieldButton
											icon="mdi/lightning-bolt"
											label="Trigger On Initialize"
											grow={true}
											onClick={triggerOnInitViewEvent}
											disabled={isEventBeingTriggered.onInit}

										/>
									</FieldGroup>
								</FieldRow>
								<FieldRow>
									<FieldGroup grow>
										<FieldButton
											icon="mdi/lightning-bolt"
											label="Trigger On Params Changed"
											grow={true}
											onClick={triggerOnParamsChangedViewEvent}
											disabled={isEventBeingTriggered.onParamsChanged}
										/>
									</FieldGroup>
								</FieldRow>
							</FieldSectionContent>
						</FieldSection>
					</Panel>
				);
			}
			case "output": {
				return (
					<Panel style={PANEL_STYLE.BASE} scroll={true}>
						<ExpressionEditor viewModel={model} />
					</Panel>
				);
			}
			default: {
				return <p>Error: Undefined panel {componentName}.</p>;
			}
		}
	};

	const baseClass = BEM("edt-module-action-editor-tester");

	return (
		<div className={baseClass.element("content").toClassName()}>
			<Layout state={layoutState} renderComponent={renderComponent} />
		</div>
	);
};
